import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { FiEdit, FiTrash2, FiMail, FiSmartphone, FiBell, FiEye } from 'react-icons/fi';
import { userRequest } from '../utils/requestMethods';
import { Header, CustomPagination, SkeletonTable } from '../components';
import { useStateContext } from '../contexts/ContextProvider';
import { toast } from 'react-toastify';
import AnnouncementModal from '../components/AnnouncementModal';
import { MdNotifications, MdClose } from 'react-icons/md';

const customStyles = {
  headRow: {
    style: {
      backgroundColor: 'rgb(248, 250, 252)',
      fontWeight: "bold",
    },
  },
};

const Announcements = () => {
  const { school } = useStateContext();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [viewingAnnouncement, setViewingAnnouncement] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('app'); // 'app', 'email', 'sms'
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total_documents: 0,
    total_pages: 0
  });

  const fetchAnnouncements = async (page = 1) => {
    try {
      setLoading(true);
      let endpoint = '';
      
      switch (activeTab) {
        case 'app':
          endpoint = `/announcement/${school._id}?page=${page}&limit=10`;
          break;
        case 'email':
          endpoint = `/communications/school/${school._id}?sort_by=date&order=desc&type=email&page=${page}&limit=10`;
          break;
        case 'sms':
          endpoint = `/communications/school/${school._id}?sort_by=date&order=desc&type=sms&page=${page}&limit=10`;
          break;
        default:
          endpoint = `/announcement/${school._id}?page=${page}&limit=10`;
      }

      const { data } = await userRequest.get(endpoint);
      
      // Handle different response structures
      if (activeTab === 'app') {
        setAnnouncements(data.data || []);
        setPagination({
          page: data.page || 1,
          limit: data.limit || 10,
          total_documents: data.total_documents || 0,
          total_pages: data.total_pages || 0
        });
      } else {
        // For email and SMS, use the new communications endpoint structure
        setAnnouncements(data.communications || []);
        setPagination({
          page: data.page || 1,
          limit: data.limit || 10,
          total_documents: data.total_documents || 0,
          total_pages: data.total_pages || 0
        });
      }
    } catch (error) {
      toast.error(`Failed to fetch ${activeTab} notifications`);
      console.error(`Error fetching ${activeTab} notifications:`, error);
      setAnnouncements([]);
      setPagination({
        page: 1,
        limit: 10,
        total_documents: 0,
        total_pages: 0
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (school?._id) {
      fetchAnnouncements();
    }
  }, [school?._id, activeTab]);

  const handleEdit = (announcement) => {
    setEditingAnnouncement(announcement);
    setIsModalOpen(true);
  };

  const handleView = (announcement) => {
    setViewingAnnouncement(announcement);
    setIsViewModalOpen(true);
  };

  const handleDelete = async (announcementId) => {
    if (window.confirm('Are you sure you want to delete this notification?')) {
      try {
        let endpoint = '';
        switch (activeTab) {
          case 'app':
            endpoint = `/announcement/${school._id}/${announcementId}`;
            break;
          case 'email':
          case 'sms':
            endpoint = `/communications/${announcementId}`;
            break;
        }
        
        await userRequest.delete(endpoint);
        toast.success('Notification deleted successfully');
        fetchAnnouncements(pagination.page);
      } catch (error) {
        toast.error('Failed to delete notification');
        console.error('Error deleting notification:', error);
      }
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingAnnouncement(null);
  };

  const handleViewModalClose = () => {
    setIsViewModalOpen(false);
    setViewingAnnouncement(null);
  };

  const getColumns = () => {
    const baseColumns = [
      {
        name: "Title/Subject",
        selector: row => (
          <div className="font-semibold text-gray-800">
            {activeTab === 'app' 
              ? row.title 
              : activeTab === 'email' 
                ? (row.metadata?.subject || 'No Subject')
                : row.message?.substring(0, 100) + (row.message?.length > 100 ? '...' : '')
            }
          </div>
        ),
        sortable: true,
      },
      /*{
        name: "Content/Message",
        selector: row => (
          <div className="text-gray-600 line-clamp-2">
            {activeTab === 'app' 
              ? row.content 
              : row.message?.substring(0, 100) + (row.message?.length > 100 ? '...' : '')
            }
          </div>
        ),
        //width: "30%",
      },*/
      {
        name: "Audience/Recipients",
        selector: row => {
          if (activeTab === 'app') {
            return (
              <div className="flex flex-wrap gap-1">
                {row.audience.map(aud => (
                  <span 
                    key={aud}
                    className="px-2 py-1 text-xs font-medium rounded-full bg-[#3F3DA5] text-white"
                  >
                    {aud}
                  </span>
                ))}
              </div>
            );
          } else {
            // For email and SMS communications
            const recipients = row.metadata?.recipients || [];
            let recipientCount = 0;
            
            if (Array.isArray(recipients)) {
              recipientCount = recipients.length;
            } else if (typeof recipients === 'string') {
              try {
                // Try to parse as JSON first
                const parsed = JSON.parse(recipients);
                recipientCount = Array.isArray(parsed) ? parsed.length : 0;
                                    } catch {
                        // If JSON parsing fails, clean up and count
                        let cleanString = recipients.replace(/^\[|\]$/g, '');
                        recipientCount = cleanString.split(',').length;
                      }
            }
            
            return (
              <div className="text-sm text-gray-600">
                {recipientCount} recipients
              </div>
            );
          }
        },
      },
      {
        name: "Date",
        selector: row => new Date(row.createdAt || row.created_at || row.sent_at || row.date).toLocaleDateString(),
        sortable: true,
      },
      {
        cell: (row) => (
          <div className="flex gap-2">
            {activeTab === 'app' ? (
              <button
                onClick={() => handleEdit(row)}
                className="p-2 text-[#3F3DA5] bg-[#C5C5E8] rounded-full hover:bg-[#3F3DA5] hover:text-white transition-colors"
                title="Edit"
              >
                <FiEdit />
              </button>
            ) : (
              <button
                onClick={() => handleView(row)}
                className="p-2 text-[#3F3DA5] bg-[#C5C5E8] rounded-full hover:bg-[#3F3DA5] hover:text-white transition-colors"
                title="View Details"
              >
                <FiEye />
              </button>
            )}
            <button
              onClick={() => handleDelete(row._id)}
              className="p-2 text-red-500 bg-red-100 rounded-full hover:bg-red-500 hover:text-white transition-colors"
              title="Delete"
            >
              <FiTrash2 />
            </button>
          </div>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
      },
    ];

    return baseColumns;
  };

  const getTabIcon = (tab) => {
    switch (tab) {
      case 'app':
        return <FiBell className="w-4 h-4" />;
      case 'email':
        return <FiMail className="w-4 h-4" />;
      case 'sms':
        return <FiSmartphone className="w-4 h-4" />;
      default:
        return <FiBell className="w-4 h-4" />;
    }
  };

  const getTabLabel = (tab) => {
    switch (tab) {
      case 'app':
        return 'App Notifications';
      case 'email':
        return 'Email Notifications';
      case 'sms':
        return 'SMS Notifications';
      default:
        return 'App Notifications';
    }
  };

  return (
    <div className="m-2 md:m-10 mt-24 p-2 md:p-10 dark:bg-secondary-dark-bg bg-white rounded-3xl min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <Header category="Communication" title="Notifications" />
        <button 
          type="button" 
          onClick={() => setIsModalOpen(true)} 
          className="inline-flex items-center justify-center py-2 px-4 border border-transparent shadow-sm text-md font-medium rounded-full text-white bg-[#3F3DA5] hover:bg-[#2E2D7A] focus:outline-none focus:ring-2 focus:ring-offset-2"
        >
          <MdNotifications className="mr-2" />
          New Notification
        </button>
      </div>

      {/* Notification Type Tabs */}
      <div className="flex mb-6 space-x-2">
        {['app', 'email', 'sms'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-xl font-medium transition-all duration-200 ${
              activeTab === tab
                ? 'bg-[#3F3DA5] text-white shadow-lg'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {getTabIcon(tab)}
            <span>{getTabLabel(tab)}</span>
          </button>
        ))}
      </div>

      <div className="rounded-2xl overflow-x-auto w-full max-w-screen-lg mx-auto">
        <div className="border rounded-3xl">
            <DataTable
            data={announcements}
            columns={getColumns()}
            getRowId={(row) => row._id}
            pagination
            paginationServer
            paginationTotalRows={pagination.total_documents}
            paginationDefaultPage={pagination.page}
            paginationPerPage={pagination.limit}
            onChangePage={(page) => fetchAnnouncements(page)}
            paginationComponent={CustomPagination}
            paginationRowsPerPageOptions={[10, 20, 30, 50]}
            customStyles={customStyles}
            progressPending={loading}
            progressComponent={<SkeletonTable rows={5} columns={6} />}
            highlightOnHover
            striped
            />
        </div>
      </div>
      {isModalOpen && (
        <AnnouncementModal 
          closeModal={handleModalClose}
          editingAnnouncement={editingAnnouncement}
          notificationType={activeTab}
          onSuccess={() => {
            fetchAnnouncements(pagination.page);
            handleModalClose();
          }}
        />
      )}

      {/* View Modal for Email/SMS Details */}
      {isViewModalOpen && viewingAnnouncement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center space-x-3">
                {viewingAnnouncement.type === 'email' && <FiMail className="text-2xl text-[#3F3DA5]" />}
                {viewingAnnouncement.type === 'sms' && <FiSmartphone className="text-2xl text-[#3F3DA5]" />}
                <h2 className="text-xl font-semibold text-gray-800">
                  {viewingAnnouncement.type === 'email' ? 'Email Details' : 'SMS Details'}
                </h2>
              </div>
              <button
                onClick={handleViewModalClose}
                className="text-gray-500 hover:text-gray-700"
              >
                <MdClose className="text-2xl" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Subject (Email only) */}
              {viewingAnnouncement.type === 'email' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject
                  </label>
                  <div className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900">
                    {viewingAnnouncement.metadata?.subject || 'No Subject'}
                  </div>
                </div>
              )}

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message
                </label>
                <div className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 min-h-[100px] whitespace-pre-wrap">
                  {viewingAnnouncement.message}
                </div>
              </div>

              {/* Recipients */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recipients ({(() => {
                    const recipients = viewingAnnouncement.metadata?.recipients || [];
                    let recipientCount = 0;
                    
                    if (Array.isArray(recipients)) {
                      recipientCount = recipients.length;
                    } else if (typeof recipients === 'string') {
                      try {
                        // Handle the specific case where recipients is a stringified array
                        const parsed = JSON.parse(recipients);
                        recipientCount = Array.isArray(parsed) ? parsed.length : 0;
                      } catch {
                        // If JSON parsing fails, clean up and count
                        let cleanString = recipients.replace(/^\[|\]$/g, '');
                        recipientCount = cleanString.split(',').length;
                      }
                    }
                    
                    return recipientCount;
                  })()})
                </label>
                <div className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 max-h-32 overflow-y-auto">
                  {(() => {
                    const recipients = viewingAnnouncement.metadata?.recipients || [];
                    let displayRecipients = [];
                    
                    if (Array.isArray(recipients)) {
                      displayRecipients = recipients;
                    } else if (typeof recipients === 'string') {
                      try {
                        // Try to parse as JSON first
                        const parsed = JSON.parse(recipients);
                        displayRecipients = Array.isArray(parsed) ? parsed : [];
                      } catch {
                        // If JSON parsing fails, clean up the string format
                        // Remove outer brackets and split by comma
                        let cleanString = recipients.replace(/^\[|\]$/g, '');
                        displayRecipients = cleanString.split(',').map(r => 
                          r.trim().replace(/^["']|["']$/g, '')
                        );
                      }
                    }
                    
                    if (displayRecipients.length === 0) {
                      return 'No recipients';
                    }
                    
                    // Display each recipient on a new line for better readability
                    return displayRecipients.map((recipient, index) => (
                      <div key={index} className="py-1 border-b border-gray-200 last:border-b-0">
                        {recipient}
                      </div>
                    ));
                  })()}
                </div>
              </div>

              {/* Attachments (Email only) */}
              {viewingAnnouncement.type === 'email' && viewingAnnouncement.metadata?.attachments?.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Attachments ({viewingAnnouncement.metadata.attachments.length})
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {viewingAnnouncement.metadata.attachments.map((attachment, index) => (
                      <div key={index} className="border border-gray-300 rounded-lg p-3">
                        <div className="aspect-square bg-gray-100 rounded-lg mb-2 overflow-hidden">
                          <img
                            src={attachment}
                            alt={`Attachment ${index + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <div className="w-full h-full flex items-center justify-center text-gray-500" style={{display: 'none'}}>
                            <FiMail className="text-2xl" />
                          </div>
                        </div>
                        <a
                          href={attachment}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-[#3F3DA5] hover:text-[#2E2D7A] truncate block"
                        >
                          View Full Size
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Status and Date */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sent Date
                  </label>
                  <div className="text-sm text-gray-900">
                    {new Date(viewingAnnouncement.createdAt).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={handleViewModalClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-full hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Announcements; 